import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Dialog, DialogTrigger, Input, Modal, ModalOverlay, SearchField } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'

import { useGlobalSearch } from './hooks/useGlobalSearch'
import type { IProps, ISearchResults, TFlatResult } from './interfaces'
import styles from './styles.module.scss'

/** Maps a group index to the flat index across all result groups */
const getResultIndex = (results: ISearchResults, type: 'company' | 'user' | 'card', groupIndex: number): number => {
  let offset = 0
  if (type === 'user') offset = results.companies.length
  else if (type === 'card') offset = results.companies.length + results.users.length

  return offset + groupIndex
}

export const CommandPalette = ({ isOpen, onClose, initialQuery = '' }: IProps): React.JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [query, setQuery] = useState<string>('')
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const { results, isLoading } = useGlobalSearch(query)

  const inputRef = useRef<HTMLInputElement>(null)
  const resultsListRef = useRef<HTMLDivElement>(null)

  const hasResults = results.users.length > 0 || results.companies.length > 0 || results.cards.length > 0

  // Build a flat list of all results for keyboard navigation
  const flatResults = useMemo((): TFlatResult[] => {
    const items: TFlatResult[] = []

    for (const company of results.companies) items.push({ type: 'company', id: company.id, item: company })

    for (const user of results.users) items.push({ type: 'user', id: user.id, item: user })

    for (const card of results.cards) items.push({ type: 'card', id: card.id, item: card })

    return items
  }, [results])

  // Reset active index when results change
  useEffect((): void => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- legitimate reset on data change
    setActiveIndex(-1)
  }, [flatResults])

  useEffect((): (() => void) | void => {
    if (isOpen) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- seed query on open
      setQuery(initialQuery)
      const timer = setTimeout((): void => {
        inputRef.current?.focus()
      }, 50)
      return (): void => clearTimeout(timer)
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- legitimate reset on close
    setQuery('')
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- legitimate reset on close
    setActiveIndex(-1)
  }, [isOpen, initialQuery])

  const handleSelect = useCallback(
    (result: TFlatResult): void => {
      onClose()
      switch (result.type) {
        case 'company':
          void navigate({ to: `/companies/${result.id}` })
          break
        case 'user': {
          if (result.item.companyId) void navigate({ to: `/companies/${result.item.companyId}/users/${result.id}` })
          break
        }
        case 'card': {
          const engravedId = result.item.engravedId ?? result.item.name ?? undefined
          const { companyId, category } = result.item
          if (companyId && category === 'day_cards') {
            void navigate({
              to: '/companies/$companyId/day-cards',
              params: { companyId },
              search: { search: engravedId },
            })
          } else if (companyId && category === 'shared_cards') {
            void navigate({
              to: '/companies/$companyId/anonymous-cards',
              params: { companyId },
              search: { search: engravedId },
            })
          } else if (companyId) {
            void navigate({
              to: '/companies/$companyId/personal-cards',
              params: { companyId },
              search: { openEngravedId: engravedId, search: engravedId },
            })
          } else {
            void navigate({
              to: '/ov-cards',
              search: { search: engravedId, openEngravedId: engravedId },
            })
          }
          break
        }
        default:
          break
      }
    },
    [navigate, onClose],
  )

  const scrollItemIntoView = useCallback((index: number): void => {
    requestAnimationFrame((): void => {
      const container = resultsListRef.current
      if (!container) return

      const items = container.querySelectorAll('[data-result-index]')
      const target = items[index]
      if (target) target.scrollIntoView({ block: 'nearest' })
    })
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (flatResults.length === 0) return

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          setActiveIndex((prev): number => {
            const next = prev < flatResults.length - 1 ? prev + 1 : 0
            scrollItemIntoView(next)
            return next
          })
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          setActiveIndex((prev): number => {
            const next = prev > 0 ? prev - 1 : flatResults.length - 1
            scrollItemIntoView(next)
            return next
          })
          break
        }
        case 'Enter': {
          e.preventDefault()
          if (activeIndex >= 0 && activeIndex < flatResults.length) handleSelect(flatResults[activeIndex])

          break
        }
        default:
          break
      }
    },
    [flatResults, activeIndex, handleSelect, scrollItemIntoView],
  )

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open): void => {
        if (!open) onClose()
      }}
    >
      <ModalOverlay className={styles.overlay} data-testid="command-palette-overlay">
        <Modal className={styles.modal} data-testid="command-palette">
          <Dialog className={styles.dialog} aria-label={t('Search')}>
            <header className={styles.header}>
              <div className={styles.headerTop}>
                <div className={styles.headerTitles}>
                  <span className={styles.headerLabel}>{t('Search')}</span>
                  <span className={styles.headerSubtitle}>{t('Find users, companies and cards')}</span>
                </div>
                <Button
                  className={styles.closeButton}
                  onPress={onClose}
                  aria-label={t('Close')}
                  data-testid="command-palette-close"
                >
                  <Icon name="close" />
                </Button>
              </div>

              <div className={styles.searchBar}>
                <Icon name="search" width="18" height="18" />
                <SearchField className={styles.searchField} value={query} onChange={setQuery} aria-label={t('Search')}>
                  <Input
                    ref={inputRef}
                    className={styles.searchInput}
                    placeholder={t('Search users, companies, cards...')}
                    data-testid="command-palette-input"
                    onKeyDown={handleKeyDown}
                  />
                </SearchField>
                <kbd className={styles.kbd}>ESC</kbd>
              </div>
            </header>

            <div className={styles.resultsList} ref={resultsListRef} aria-label={t('Search results')}>
              {isLoading && (
                <div className={styles.loadingState} data-testid="command-palette-loading">
                  <span className={styles.spinner} />
                  <span>{t('Searching...')}</span>
                </div>
              )}

              {!isLoading && query.trim().length >= 2 && !hasResults && (
                <div className={styles.emptyState} data-testid="command-palette-empty">
                  {t('No results found for "{{ query }}"', { query: query.trim() })}
                </div>
              )}

              {!isLoading && query.trim().length < 2 && (
                <div className={styles.hintState}>
                  <Icon name="search" width="20" height="20" />
                  <span>{t('Type at least 2 characters to search')}</span>
                </div>
              )}

              {results.companies.length > 0 && (
                <div className={styles.resultGroup}>
                  <div className={styles.groupLabel}>{t('Companies')}</div>
                  {results.companies.map((company, i) => {
                    const flatIndex = getResultIndex(results, 'company', i)

                    return (
                      <Button
                        key={company.id}
                        className={clsx(styles.resultItem, activeIndex === flatIndex && styles.resultItemActive)}
                        onPress={(): void => handleSelect({ type: 'company', id: company.id, item: company })}
                        data-testid="command-palette-result-company"
                        data-result-index={flatIndex}
                        {...(activeIndex === flatIndex && { 'data-active': true })}
                      >
                        <span className={styles.resultIcon} data-entity="company">
                          <Icon name="building" width="16" height="16" />
                        </span>
                        <div className={styles.resultContent}>
                          <span className={styles.resultName}>{company.name ?? '—'}</span>
                        </div>
                        <Icon name="chevron-right" width="14" height="14" className={styles.resultArrow} />
                      </Button>
                    )
                  })}
                </div>
              )}

              {results.users.length > 0 && (
                <div className={styles.resultGroup}>
                  <div className={styles.groupLabel}>{t('Users')}</div>
                  {results.users.map((user, i) => {
                    const flatIndex = getResultIndex(results, 'user', i)

                    return (
                      <Button
                        key={user.id}
                        className={clsx(styles.resultItem, activeIndex === flatIndex && styles.resultItemActive)}
                        onPress={(): void => handleSelect({ type: 'user', id: user.id, item: user })}
                        data-testid="command-palette-result-user"
                        data-result-index={flatIndex}
                        {...(activeIndex === flatIndex && { 'data-active': true })}
                      >
                        <span className={styles.resultIcon} data-entity="user">
                          <Icon name="users" width="16" height="16" />
                        </span>
                        <div className={styles.resultContent}>
                          <span className={styles.resultName}>
                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                          </span>
                          {user.companyName && <span className={styles.resultMeta}>{user.companyName}</span>}
                        </div>
                        <Icon name="chevron-right" width="14" height="14" className={styles.resultArrow} />
                      </Button>
                    )
                  })}
                </div>
              )}

              {results.cards.length > 0 && (
                <div className={styles.resultGroup}>
                  <div className={styles.groupLabel}>{t('Cards')}</div>
                  {results.cards.map((card, i) => {
                    const flatIndex = getResultIndex(results, 'card', i)

                    return (
                      <Button
                        key={card.id}
                        className={clsx(styles.resultItem, activeIndex === flatIndex && styles.resultItemActive)}
                        onPress={(): void => handleSelect({ type: 'card', id: card.id, item: card })}
                        data-testid="command-palette-result-card"
                        data-result-index={flatIndex}
                        {...(activeIndex === flatIndex && { 'data-active': true })}
                      >
                        <span className={styles.resultIcon} data-entity="card">
                          <Icon name="credit-card" width="16" height="16" />
                        </span>
                        <div className={styles.resultContent}>
                          <span className={styles.resultName}>{card.engravedId ?? card.name ?? '—'}</span>
                          {card.companyName && <span className={styles.resultMeta}>{card.companyName}</span>}
                        </div>
                        <Icon name="chevron-right" width="14" height="14" className={styles.resultArrow} />
                      </Button>
                    )
                  })}
                </div>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  )
}
